import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sparkles,
  Loader2,
  Copy,
  Check,
  Save,
  ShieldCheck,
  ShieldAlert,
  Hash,
  Lightbulb,
  RefreshCw,
  Mic,
  Square,
  Newspaper,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useTeamProfiles, useHookLibrary, useGenerateContent, useSaveGeneration } from "@/hooks/useContentStudio";
import type { NewsSource, SocialPostSource, CampaignSource, AgentContentResponse, AgentContentRequest } from "@/types/content-studio";
import { toast } from "sonner";
import { useVoiceRecording } from "@/hooks/useVoiceRecording";

interface GenerationPanelProps {
  selectedNews: NewsSource[];
  selectedSocial: SocialPostSource[];
  selectedCampaigns: CampaignSource[];
  customInput: string;
}

export function GenerationPanel({
  selectedNews,
  selectedSocial,
  selectedCampaigns,
  customInput,
}: GenerationPanelProps) {
  const { data: teamProfiles } = useTeamProfiles();
  const { data: hooks } = useHookLibrary();
  const generateMutation = useGenerateContent();
  const saveMutation = useSaveGeneration();

  const [senderId, setSenderId] = useState<string>("");
  const [hookType, setHookType] = useState<string>("auto");
  const [targetCompany, setTargetCompany] = useState<string>("");
  const [contextNotes, setContextNotes] = useState<string>("");
  const [result, setResult] = useState<AgentContentResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [includeNewsContext, setIncludeNewsContext] = useState(true);

  const handleTranscript = useCallback((transcript: string) => {
    setContextNotes((prev) => (prev ? prev + "\n" + transcript : transcript));
  }, []);

  const { isRecording, isTranscribing, formattedTime, toggleRecording } = useVoiceRecording({
    maxDurationSeconds: 180,
    onTranscriptReady: handleTranscript,
  });

  const totalSources = selectedNews.length + selectedSocial.length + selectedCampaigns.length + (customInput.length > 0 ? 1 : 0);

  function buildContextString(): string {
    const parts: string[] = [];

    if (selectedNews.length > 0) {
      parts.push("=== NEWS ARTICLES ===");
      selectedNews.forEach((n, i) => {
        parts.push(`[Article ${i + 1}] ${n.title}`);
        if (n.signal_type) parts.push(`Type: ${n.signal_type}`);
        if (n.is_competitor_alert && includeNewsContext) parts.push("⚠ COMPETITOR ALERT");
        if (n.is_prospect_opportunity && includeNewsContext) {
          parts.push(`🎯 PROSPECT OPPORTUNITY${n.prospect_company_name ? ": " + n.prospect_company_name : ""}`);
        }

        if (includeNewsContext) {
          if (n.enriched_content) {
            parts.push(`AI Insight: ${n.enriched_content}`);
          } else if (n.executive_briefing) {
            parts.push(`Executive Briefing: ${n.executive_briefing}`);
          } else if (n.summary) {
            parts.push(n.summary);
          }
          const meta = n.enrichment_metadata;
          if (meta?.why_it_matters) parts.push(`Why It Matters: ${meta.why_it_matters}`);
          if (n.market_context) parts.push(`Market Context: ${n.market_context}`);
          if (n.opportunity_analysis) parts.push(`Opportunity: ${n.opportunity_analysis}`);
          if (meta?.recommended_action) parts.push(`Recommended Action: ${meta.recommended_action}`);
          if (n.outreach_guidance) parts.push(`Outreach Guidance: ${n.outreach_guidance}`);
          if (n.companies_mentioned?.length) parts.push(`Companies: ${n.companies_mentioned.join(", ")}`);
          if (n.matched_keywords?.length) parts.push(`Keywords: ${n.matched_keywords.join(", ")}`);
        } else {
          if (n.short_summary || n.summary) parts.push((n.short_summary || n.summary)!);
        }
        parts.push("");
      });
    }

    if (selectedSocial.length > 0) {
      parts.push("=== SOCIAL POSTS ===");
      selectedSocial.forEach((s, i) => {
        parts.push(`[Post ${i + 1}] by ${s.poster_name || "Unknown"} (${s.likes} likes, ${s.comments} comments)`);
        if (s.full_content) parts.push(s.full_content);
        parts.push("");
      });
    }

    if (selectedCampaigns.length > 0) {
      parts.push("=== CAMPAIGN CONTEXT ===");
      selectedCampaigns.forEach((c) => {
        parts.push(`Campaign: ${c.campaign_name}`);
        if (c.description) parts.push(c.description);
        const stats: string[] = [];
        if (c.linkedin_sent != null) stats.push(`Sent: ${c.linkedin_sent}`);
        if (c.linkedin_accepted != null) stats.push(`Accepted: ${c.linkedin_accepted}`);
        if (c.linkedin_replied != null) stats.push(`Replied: ${c.linkedin_replied}`);
        if (stats.length) parts.push(`Performance: ${stats.join(", ")}`);
        parts.push("");
      });
    }

    if (customInput) {
      parts.push("=== CUSTOM INPUT ===");
      parts.push(customInput);
    }

    if (contextNotes) {
      parts.push("=== ADDITIONAL NOTES ===");
      parts.push(contextNotes);
    }

    return parts.join("\n");
  }

  async function handleGenerate() {
    const selectedHook = hookType !== "auto" ? hooks?.find((h) => h.hook_type === hookType) : null;

    const request: AgentContentRequest = {
      content_type: "linkedin_post",
      brand: "carr_bio",
      hook_type: selectedHook?.hook_type || undefined,
      target_company: targetCompany || undefined,
      context: buildContextString() || undefined,
      max_length: 3000,
      sources: {
        news: selectedNews.map((n) => ({
          title: n.title,
          summary: includeNewsContext
            ? (n.enriched_content || n.executive_briefing || n.summary || undefined)
            : (n.short_summary || n.summary || undefined),
          url: n.url || undefined,
          enriched_content: includeNewsContext ? (n.enriched_content || undefined) : undefined,
          market_context: includeNewsContext ? (n.market_context || undefined) : undefined,
          opportunity_analysis: includeNewsContext ? (n.opportunity_analysis || undefined) : undefined,
          outreach_guidance: includeNewsContext ? (n.outreach_guidance || undefined) : undefined,
          why_it_matters: includeNewsContext ? (n.enrichment_metadata?.why_it_matters || undefined) : undefined,
          companies_mentioned: includeNewsContext ? (n.companies_mentioned || undefined) : undefined,
          is_competitor_alert: n.is_competitor_alert || undefined,
          is_prospect_opportunity: n.is_prospect_opportunity || undefined,
          prospect_company_name: n.prospect_company_name || undefined,
        })),
        social: selectedSocial.map((s) => ({
          content: s.full_content || s.content_preview || "",
          author: s.poster_name || undefined,
          url: s.post_url || undefined,
        })),
        custom_input: customInput || undefined,
      },
    };

    try {
      const data = await generateMutation.mutateAsync(request);
      setResult(data);
    } catch (err: any) {
      toast.error("Generation failed", {
        description: err.message || "Something went wrong",
      });
    }
  }

  async function handleCopy() {
    if (!result?.body) return;
    const text = [
      result.hook,
      result.body,
      result.call_to_action,
      result.hashtags?.length ? result.hashtags.map((h) => `#${h}`).join(" ") : null,
    ]
      .filter(Boolean)
      .join("\n\n");

    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  async function handleSave() {
    if (!result) return;
    try {
      await saveMutation.mutateAsync({
        content_type: "linkedin_post",
        generated_content: result.body,
        team_profile_id: senderId || null,
        hook_type: hookType === "auto" ? null : (hookType || null),
        source_custom_input: customInput || null,
        context_notes: contextNotes || null,
      });
      toast.success("Saved", { description: "Content saved to generation history" });
    } catch (err: any) {
      toast.error("Save failed", { description: err.message });
    }
  }

  return (
    <div className="space-y-4">
      {/* Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Generation Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Sender */}
          <div className="space-y-1">
            <Label className="text-xs">Sender</Label>
            <Select value={senderId} onValueChange={setSenderId}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select sender..." />
              </SelectTrigger>
              <SelectContent>
                {teamProfiles?.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-xs">
                    {p.name} {p.title ? `\u2014 ${p.title}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hook Type */}
          <div className="space-y-1">
            <Label className="text-xs">Hook Style</Label>
            <Select value={hookType} onValueChange={setHookType}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Select hook..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto" className="text-xs">Auto (let AI choose)</SelectItem>
                {hooks?.map((h) => (
                  <SelectItem key={h.hook_type} value={h.hook_type} className="text-xs">
                    {h.hook_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Target Company */}
          <div className="space-y-1">
            <Label className="text-xs">Target Company (optional)</Label>
            <Input
              placeholder="e.g. Lonza, Charles River..."
              value={targetCompany}
              onChange={(e) => setTargetCompany(e.target.value)}
              className="h-8 text-xs"
            />
          </div>

          {/* Include News Context Toggle */}
          {selectedNews.length > 0 && (
            <div className="flex items-center justify-between py-1">
              <div className="flex items-center gap-1.5">
                <Newspaper className="h-3.5 w-3.5 text-primary" />
                <Label className="text-xs">Include News Intelligence</Label>
              </div>
              <Switch
                checked={includeNewsContext}
                onCheckedChange={setIncludeNewsContext}
              />
            </div>
          )}
          {selectedNews.length > 0 && (
            <p className="text-[10px] text-muted-foreground -mt-1">
              {includeNewsContext
                ? "Full enrichment data (AI insights, market context, companies) sent to AI"
                : "Only article titles and summaries sent — lighter generation"}
            </p>
          )}

          {/* Context Notes */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Context Notes</Label>
              <div className="flex items-center gap-1.5">
                {isRecording && (
                  <span className="text-[10px] font-mono text-red-500 animate-pulse">
                    {formattedTime}
                  </span>
                )}
                {isTranscribing && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin" /> Transcribing...
                  </span>
                )}
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "ghost"}
                  size="icon"
                  className="h-6 w-6"
                  onClick={toggleRecording}
                  disabled={isTranscribing}
                  title={isRecording ? "Stop recording" : "Record voice note"}
                >
                  {isRecording ? (
                    <Square className="h-3 w-3" />
                  ) : (
                    <Mic className="h-3 w-3" />
                  )}
                </Button>
              </div>
            </div>
            <Textarea
              placeholder={isRecording ? "Listening... click stop when done" : "Type notes or tap the mic to dictate..."}
              value={contextNotes}
              onChange={(e) => setContextNotes(e.target.value)}
              className={`min-h-[60px] text-xs resize-y ${isRecording ? "border-red-300 bg-red-50/50" : ""}`}
            />
          </div>

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
            className="w-full gap-2"
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating via Claude Sonnet...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                Generate LinkedIn Post
                {totalSources > 0 && (
                  <Badge variant="secondary" className="ml-1 text-[10px]">
                    {totalSources} source{totalSources !== 1 ? "s" : ""}
                  </Badge>
                )}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Output */}
      {result && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Generated Post</CardTitle>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={handleGenerate} disabled={generateMutation.isPending} className="h-7 gap-1 text-xs">
                  <RefreshCw className="h-3 w-3" /> Regen
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCopy} className="h-7 gap-1 text-xs">
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                <Button variant="ghost" size="sm" onClick={handleSave} disabled={saveMutation.isPending} className="h-7 gap-1 text-xs">
                  <Save className="h-3 w-3" /> Save
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Hook */}
            {result.hook && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                  <Lightbulb className="h-3 w-3" /> Hook
                </p>
                <p className="text-sm font-medium text-foreground">{result.hook}</p>
              </div>
            )}

            {/* Body */}
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground mb-1">Body</p>
              <div className="text-sm text-foreground whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-md p-3">
                {result.body}
              </div>
            </div>

            {/* CTA */}
            {result.call_to_action && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground mb-1">Call to Action</p>
                <p className="text-sm text-foreground">{result.call_to_action}</p>
              </div>
            )}

            {/* Hashtags */}
            {result.hashtags?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                  <Hash className="h-3 w-3" /> Hashtags
                </p>
                <div className="flex flex-wrap gap-1">
                  {result.hashtags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-[10px]">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Personalization Points */}
            {result.personalization_points?.length > 0 && (
              <div>
                <p className="text-[10px] font-semibold text-muted-foreground mb-1">Personalization</p>
                <ul className="text-xs text-muted-foreground space-y-0.5">
                  {result.personalization_points.map((p, i) => (
                    <li key={i} className="flex items-start gap-1">
                      <span className="text-primary mt-0.5">&bull;</span> {p}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Brand Compliance */}
            {result.brand_compliance && (
              <div className="flex items-center gap-2 pt-2 border-t">
                {result.brand_compliance.is_compliant ? (
                  <Badge variant="outline" className="text-[10px] gap-1 bg-green-50 text-green-700 border-green-200">
                    <ShieldCheck className="h-3 w-3" /> Brand Compliant
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] gap-1 bg-yellow-50 text-yellow-700 border-yellow-200">
                    <ShieldAlert className="h-3 w-3" /> Review Needed
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px]">
                  Voice: {result.brand_compliance.voice_match}
                </Badge>
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {result.word_count} words
                </span>
              </div>
            )}

            {/* Sources Used */}
            {result.data_sources_used?.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-[10px] font-semibold text-muted-foreground mb-1">Agent Sources Used</p>
                <div className="flex flex-wrap gap-1">
                  {result.data_sources_used.map((s, i) => (
                    <Badge key={i} variant="outline" className="text-[9px] bg-muted">
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
